<template>
    <div>
        <ts-page-title
            :title="$t('zone.assignDriver')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('zone.pageTitle'), href: '/admin/driver/zones' },
                { text: $t('zone.assignDriver'), active: true }
            ]"
        />
        <div class="tw-mb-4">
            <button
                @click="goBack"
                class="tw-group tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-200 dark:tw-border-slate-700 tw-rounded-md tw-shadow-sm tw-text-slate-600 dark:tw-text-slate-300 hover:tw-bg-slate-50 dark:hover:tw-bg-slate-750 hover:tw-text-blue-600 dark:hover:tw-text-blue-400 tw-transition-all tw-duration-200"
            >
                <span
                    class="material-symbols-outlined tw-transform group-hover:tw--translate-x-1 tw-transition-transform tw-duration-200"
                    style="font-size: 18px; margin-right: 6px; color: inherit;"
                >
                    arrow_back
                </span>
                <span class="tw-text-sm tw-font-semibold font-work-sans">
                    Back
                </span>
            </button>
        </div>

        <main class="tw-flex-1">
            <div class="tw-flex tw-flex-col tw-gap-8">
                <!-- Header Section -->
                <div
                    class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-6 tw-mb-6"
                >
                    <div class="lg:tw-col-span-7">
                        <div
                            class="tw-flex tw-items-center tw-gap-4 tw-bg-white dark:tw-bg-gray-800 tw-py-3 tw-px-5 tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-w-full"
                        >
                            <div
                                class="tw-bg-blue-50 dark:tw-bg-blue-900/30 tw-w-16 tw-h-16 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                            >
                                <span
                                    class="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400 tw-text-[32px] [font-variation-settings:'FILL'_1]"
                                >
                                    location_pin
                                </span>
                            </div>

                            <div class="tw-flex-1 tw-min-w-0">
                                <label
                                    class="tw-text-xs tw-uppercase tw-font-bold tw-text-gray-400 tw-tracking-wider tw-mb-1 tw-block"
                                >
                                    Current Zone
                                </label>
                                <div class="tw-relative">
                                    <a-select
                                        show-search
                                        v-model.number="model.zone_id"
                                        placeholder="Select a zone"
                                        class="tw-w-full"
                                        :filter-option="filterZoneOption"
                                        option-filter-prop="children"
                                    >
                                        <a-select-option
                                            v-for="zone in zones"
                                            :key="zone.id"
                                            :value="zone.id"
                                        >
                                            {{ zone.name }}
                                        </a-select-option>
                                    </a-select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="lg:tw-col-span-5 tw-hidden lg:tw-block"></div>
                </div>
                <!-- Main Content Grid -->
                <div
                    class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-6 tw-items-start tw-pb-20"
                >
                    <!-- Left Column - Shop List -->
                    <div class="lg:tw-col-span-7 tw-flex tw-flex-col tw-gap-6">
                        <!-- Search & Filter -->
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <div class="tw-relative tw-flex-1">
                                <span
                                    class="material-symbols-outlined tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none"
                                >
                                    search
                                </span>
                                <input
                                    v-model="searchQuery"
                                    class="tw-w-full tw-pl-10 tw-pr-4 tw-py-2.5 tw-leading-5 tw-bg-white dark:tw-bg-gray-800 tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-text-sm focus:tw-outline-none dark:tw-text-white"
                                    placeholder="Search shop name or address..."
                                    type="text"
                                />
                            </div>
                        </div>

                        <!-- Shop Cards -->
                        <ts-loading-banner :loading="loading">
                            <div class="tw-flex tw-flex-col tw-gap-4">
                                <div
                                    v-for="shop in displayShops"
                                    :key="shop.id"
                                    :class="[
                                        'tw-bg-white dark:tw-bg-gray-800 tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-overflow-hidden tw-shadow-sm tw-group hover:tw-border-blue-500 tw-transition-colors',

                                        isShopSelected(shop)
                                            ? 'tw-border-blue-500 tw-ring-2 tw-ring-blue-200 dark:tw-ring-blue-900'
                                            : ''
                                    ]"
                                >
                                    <div class="tw-p-5 tw-flex tw-gap-4">
                                        <div
                                            class="tw-w-14 tw-h-14 tw-rounded-xl tw-bg-gray-100 dark:tw-bg-gray-700 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-flex-shrink-0"
                                        >
                                            <span
                                                class="material-symbols-outlined tw-text-2xl"
                                            >
                                                store
                                            </span>
                                        </div>

                                        <div class="tw-flex-1 tw-min-w-0">
                                            <div
                                                class="tw-flex tw-justify-between tw-items-start tw-mb-1"
                                            >
                                                <div>
                                                    <h3
                                                        class="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white tw-truncate"
                                                    >
                                                        {{ shop.name }}
                                                    </h3>
                                                </div>
                                                <button
                                                    @click="
                                                        toggleShopSelection(
                                                            shop.id
                                                        )
                                                    "
                                                    :class="[
                                                        'tw-text-sm tw-font-medium tw-px-3 tw-py-1.5 tw-rounded-lg tw-transition-colors',
                                                        isShopSelected(shop)
                                                            ? 'tw-text-blue-600 tw-bg-blue-100 dark:tw-bg-blue-900'
                                                            : 'tw-text-gray-500 tw-bg-gray-100 dark:tw-bg-gray-700'
                                                    ]"
                                                >
                                                    {{
                                                        isShopSelected(shop)
                                                            ? "Selected"
                                                            : "Select Shop"
                                                    }}
                                                </button>
                                            </div>

                                            <div class="tw-mt-4 tw-space-y-2">
                                                <div
                                                    v-for="order in shop.orders"
                                                    :key="order.id"
                                                    :class="[
                                                        'tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-border',
                                                        order.urgent
                                                            ? 'tw-bg-red-50 dark:tw-bg-red-900 tw-bg-opacity-10 tw-border-red-100 dark:tw-border-red-900'
                                                            : 'tw-bg-gray-50 dark:tw-bg-gray-800 tw-bg-opacity-50 tw-border-gray-100 dark:tw-border-gray-700'
                                                    ]"
                                                >
                                                    <div class="tw-flex-1">
                                                        <div
                                                            class="tw-flex tw-items-center tw-gap-2 tw-mb-1"
                                                        >
                                                            <span
                                                                class="tw-font-mono tw-text-xs tw-font-bold tw-text-gray-700 dark:tw-text-gray-300"
                                                            >
                                                                Requested For
                                                            </span>
                                                            <span
                                                                class="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-font-bold"
                                                            >
                                                                {{
                                                                    order.items
                                                                }}
                                                            </span>
                                                        </div>
                                                        <div
                                                            class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-flex tw-gap-2"
                                                        >
                                                            <span
                                                                :class="
                                                                    order.urgent
                                                                        ? 'tw-text-red-500 tw-font-medium'
                                                                        : ''
                                                                "
                                                            >
                                                                {{
                                                                    order.timeAgo
                                                                }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <input
                                                        v-model="selectedOrders"
                                                        :value="order.id"
                                                        class="tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-blue-500 tw-w-4 tw-h-4"
                                                        type="checkbox"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Empty State -->
                                <div
                                    v-if="displayShops.length === 0"
                                    class="tw-text-center tw-py-12"
                                >
                                    <p
                                        class="tw-mt-4 tw-text-gray-500 dark:tw-text-gray-400"
                                    >
                                        No shops found
                                    </p>
                                </div>
                            </div>
                        </ts-loading-banner>
                    </div>

                    <!-- Right Column - Summary & Driver Selection -->
                    <div
                        class="lg:tw-col-span-5 tw-flex tw-flex-col tw-gap-6 tw-sticky tw-top-6"
                    >
                        <!-- Selection Summary -->
                        <div
                            class="tw-bg-white dark:tw-bg-gray-800 tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-sm tw-flex tw-flex-col tw-overflow-hidden"
                        >
                            <div
                                class="tw-p-4 tw-border-b tw-border-gray-200 dark:tw-border-gray-700 tw-bg-gray-50 dark:tw-bg-gray-700 tw-bg-opacity-50"
                            >
                                <h3
                                    class="tw-font-bold tw-text-gray-900 dark:tw-text-white tw-flex tw-items-center tw-gap-2"
                                >
                                    <span
                                        class="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400"
                                        >shopping_cart_checkout</span
                                    >
                                    Selection Summary
                                </h3>
                                <p class="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Review items before assigning a driver.
                                </p>
                            </div>

                            <div
                                class="tw-overflow-y-auto tw-p-4 tw-flex tw-flex-col tw-gap-3 tw-max-h-96"
                            >
                                <!-- Selected Orders by Shop -->
                                <div
                                    v-for="shop in selectedShopsSummary"
                                    :key="shop.id"
                                    class="tw-border tw-border-blue-200 dark:tw-border-blue-900 tw-bg-blue-50 dark:tw-bg-blue-900 tw-bg-opacity-30 tw-rounded-lg tw-p-3"
                                >
                                    <div
                                        class="tw-flex tw-justify-between tw-items-center tw-mb-2"
                                    >
                                        <span
                                            class="tw-text-xs tw-font-bold tw-text-gray-700 dark:tw-text-gray-300"
                                        >
                                            {{ shop.name }}
                                        </span>
                                        <div
                                            class="tw-flex tw-items-center tw-gap-2"
                                        >
                                            <span
                                                class="tw-text-xs tw-bg-blue-600 tw-text-white tw-px-1.5 tw-py-0.5 tw-rounded"
                                            >
                                                {{ shop.orderCount }} Orders
                                            </span>
                                            <button
                                                @click="
                                                    removeShopOrders(shop.id)
                                                "
                                                class="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded hover:tw-bg-red-50 dark:hover:tw-bg-red-900 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors"
                                                title="Remove"
                                            >
                                                <span
                                                    class="material-symbols-outlined tw-text-base"
                                                    >close</span
                                                >
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        class="tw-space-y-1 tw-pl-2 tw-border-l-2 tw-border-blue-200 dark:tw-border-blue-900"
                                    >
                                        <div
                                            v-for="order in shop.orders"
                                            :key="order.id"
                                            class="tw-flex tw-justify-between tw-text-xs tw-text-gray-600 dark:tw-text-gray-400"
                                        >
                                            <span>{{ order.orderNumber }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Empty State -->
                                <div
                                    v-if="selectedOrders.length === 0"
                                    class="tw-border tw-border-dashed tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-gray-400 dark:tw-text-gray-500"
                                >
                                    <span
                                        class="material-symbols-outlined tw-text-3xl tw-mb-2 tw-opacity-50"
                                        >add_shopping_cart</span
                                    >
                                    <span class="tw-text-xs"
                                        >Select orders from the list</span
                                    >
                                </div>
                            </div>

                            <div
                                class="tw-p-4 tw-border-t tw-border-gray-200 dark:tw-border-gray-700 tw-bg-gray-50 dark:tw-bg-gray-800 tw-bg-opacity-50"
                            >
                                <div
                                    class="tw-flex tw-justify-between tw-items-center"
                                >
                                    <p
                                        class="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-bold tw-tracking-wider"
                                    >
                                        Est. Stops
                                    </p>
                                    <p
                                        class="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white"
                                    >
                                        {{ selectedShopsSummary.length }}
                                        {{
                                            selectedShopsSummary.length === 1
                                                ? "Shop"
                                                : "Shops"
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Driver Selection -->
                        <div
                            class="tw-bg-white dark:tw-bg-gray-800 tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-sm tw-flex tw-flex-col tw-overflow-hidden"
                        >
                            <div
                                class="tw-p-4 tw-border-b tw-border-gray-200 dark:tw-border-gray-700 tw-bg-gray-50 dark:tw-bg-gray-700 tw-bg-opacity-50"
                            >
                                <h3
                                    class="tw-font-bold tw-text-gray-900 dark:tw-text-white tw-flex tw-items-center tw-gap-2"
                                >
                                    <span
                                        class="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400"
                                        >badge</span
                                    >
                                    Selected Driver
                                </h3>
                            </div>

                            <div class="tw-p-4">
                                <div class="tw-mb-4">
                                    <label
                                        class="tw-text-xs tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-mb-1.5 tw-block"
                                    >
                                        Assign Driver
                                    </label>
                                    <div class="tw-relative">
                                        <a-select
                                            show-search
                                            v-model="selectedDriver"
                                            placeholder="Search driver name..."
                                            class="tw-w-full"
                                            :filter-option="filterDriverOption"
                                            option-filter-prop="children"
                                            size="large"
                                        >
                                            <a-select-option
                                                v-for="driver in availableDrivers"
                                                :key="driver.driver_id"
                                                :value="driver.driver_id"
                                            >
                                                {{ driver.driver_name }}
                                            </a-select-option>
                                        </a-select>
                                    </div>

                                    <!-- Selected Driver Info -->
                                    <div
                                        v-if="selectedDriverInfo"
                                        class="tw-mt-3 tw-flex tw-items-center tw-gap-3 tw-p-2 tw-bg-gray-50 dark:tw-bg-gray-700 tw-rounded-lg"
                                    >
                                        <img
                                            src="@/assets/driverDefault.png"
                                            alt="Driver"
                                            class="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover tw-flex-shrink-0"
                                        />

                                        <div
                                            class="tw-flex-1 tw-flex tw-items-center tw-min-w-0"
                                        >
                                            <p
                                                class="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-truncate tw-leading-none tw-m-0"
                                            >
                                                {{
                                                    selectedDriverInfo.driver_name
                                                }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    @click="assignDriver"
                                    :disabled="!canAssign"
                                    :class="[
                                        'tw-w-full tw-font-bold tw-py-3 tw-px-4 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-colors tw-shadow-lg',
                                        canAssign
                                            ? 'tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-shadow-blue-500 tw-shadow-opacity-30'
                                            : 'tw-bg-gray-300 dark:tw-bg-gray-700 tw-text-gray-500 dark:tw-text-gray-500 tw-cursor-not-allowed'
                                    ]"
                                >
                                    <span>Assign Pickup</span>
                                    <span
                                        class="material-symbols-outlined tw-text-lg"
                                        >send</span
                                    >
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { Errors } from "form-backend-validation";
import moment from "moment";
export default {
    name: "AssignDriver",
    data() {
        return {
            loading: false,
            searchQuery: "",
            selectedDriver: "",
            availableDrivers: [],
            selectedOrders: [],
            zones: [],
            fetch_data: false,
            model: {
                zone_id: null,
                driver_id: null,
                shop_ids: [], // Array to hold selected shop IDs
                pickup_request_ids: [] // Array to hold selected order IDs
            }
        };
    },
    mounted() {
        this.fetchData();
        this.getDrivers();
    },
    computed: {
        ...mapState("driver/zone", ["resources"]),

        // zones() {
        //     return this.resources || [];
        // },

        // selectedShopsSummary() {
        //     const shopMap = {};
        //     this.selectedOrders.forEach(orderId => {
        //         const shop = this.displayShops.find(s =>
        //             s.orders.some(o => o.id === orderId)
        //         );

        //         if (shop) {
        //             if (!shopMap[shop.id]) {
        //                 shopMap[shop.id] = {
        //                     id: shop.id,
        //                     name: shop.name,
        //                     orders: [],
        //                     orderCount: 0
        //                 };
        //             }

        //             const order = shop.orders.find(o => o.id === orderId);
        //             if (order) {
        //                 shopMap[shop.id].orders.push(order);
        //                 shopMap[shop.id].orderCount++;
        //             }
        //         }
        //     });
        //     return Object.values(shopMap);
        // },

        selectedShopsSummary() {
            const shopMap = {};

            this.selectedOrders.forEach(orderId => {
                // Search through ALL zones, not just displayShops (current zone only)
                let foundShop = null;
                let foundOrder = null;

                // Loop through all zones to find the shop that has this order
                for (const zone of this.zones) {
                    if (!zone.active_shops) continue;

                    for (const shop of zone.active_shops) {
                        const order = (shop.pickup_requests || []).find(
                            req => req.pickup_request_id === orderId
                        );

                        if (order) {
                            foundShop = shop;
                            foundOrder = order;
                            break;
                        }
                    }
                    if (foundShop) break;
                }

                if (foundShop && foundOrder) {
                    if (!shopMap[foundShop.shop_id]) {
                        shopMap[foundShop.shop_id] = {
                            id: foundShop.shop_id,
                            name: foundShop.shop_name,
                            orders: [],
                            orderCount: 0
                        };
                    }

                    shopMap[foundShop.shop_id].orders.push({
                        id: foundOrder.pickup_request_id,
                        orderNumber: foundOrder.pickup_request_id
                            .toString()
                            .substring(0, 8)
                            .toUpperCase()
                    });
                    shopMap[foundShop.shop_id].orderCount++;
                }
            });

            return Object.values(shopMap);
        },

        selectedDriverInfo() {
            if (!this.selectedDriver) return null;
            return this.availableDrivers.find(
                d => d.driver_id === this.selectedDriver
            );
        },

        canAssign() {
            // Updated to ensure it checks the length of the selectedOrders array
            return this.selectedOrders.length > 0 && this.selectedDriver !== "";
        },

        currentZone() {
            const queryId = Number(this.$route.query.id);
            return this.zones.find(z => z.id === queryId) || null;
        },

        // displayShops() {
        //     if (!this.currentZone || !this.currentZone.active_shops) return [];
        //     return this.currentZone.active_shops
        //         .map(shop => {
        //             const formattedOrders = (shop.pickup_requests || []).map(
        //                 req => {
        //                     // 1. Replace the space with 'T' to make it ISO 8601 compliant (YYYY-MM-DDTHH:mm:ss)
        //                     // This is much safer than manual splitting and reordering
        //                     const isoDateString = req.request_time.replace(
        //                         " ",
        //                         "T"
        //                     );
        //                     const date = new Date(isoDateString);

        //                     // 2. Calculate difference in minutes
        //                     const now = new Date();
        //                     const diffMs = now - date;
        //                     const mins = Math.floor(diffMs / 60000);

        //                     // 3. Handle potential negative values (if server time is slightly ahead of local time)
        //                     const absoluteMins = Math.max(0, mins);

        //                     return {
        //                         id: req.pickup_request_id,
        //                         orderNumber: req.pickup_request_id
        //                             .toString()
        //                             .substring(0, 8)
        //                             .toUpperCase(),
        //                         items: req.package_type
        //                             ? req.package_type.package_type_kh
        //                             : "",
        //                         timeAgo: isNaN(mins)
        //                             ? "Just now"
        //                             : absoluteMins < 1
        //                             ? "Just now"
        //                             : absoluteMins < 60
        //                             ? `${absoluteMins}m ago`
        //                             : `${Math.floor(absoluteMins / 60)}h ago`,
        //                         status: "Ready",
        //                         urgent: false
        //                     };
        //                 }
        //             );

        //             return {
        //                 id: shop.shop_id,
        //                 name: shop.shop_name,
        //                 logo: shop.photo,
        //                 address: shop.location_name,
        //                 orders: formattedOrders
        //             };
        //         })
        //         .filter(shop => shop.orders.length > 0);
        // }
        displayShops() {
            if (!this.currentZone || !this.currentZone.active_shops) return [];

            return this.currentZone.active_shops
                .map(shop => {
                    const formattedOrders = (shop.pickup_requests || []).map(
                        req => {
                            // 1. Parse using the exact format of your string: Day-Month-Year Hour:Minute AM/PM
                            const m = moment(
                                req.request_time,
                                "DD-MM-YYYY hh:mm A"
                            );

                            let timeAgo = "Just now";

                            if (m.isValid()) {
                                timeAgo = m.fromNow();

                                // 2. Fix for "in a few seconds" (occurs if server time is slightly ahead)
                                if (timeAgo.includes("in ")) {
                                    timeAgo = "Just now";
                                }
                            }

                            return {
                                id: req.pickup_request_id,
                                orderNumber: req.pickup_request_id
                                    .toString()
                                    .substring(0, 8)
                                    .toUpperCase(),
                                items: req.package_type
                                    ? req.package_type.package_type_kh
                                    : "",
                                timeAgo: timeAgo,
                                status: "Ready",
                                urgent: false
                            };
                        }
                    );

                    return {
                        id: shop.shop_id,
                        name: shop.shop_name,
                        logo: shop.photo,
                        address: shop.location_name,
                        orders: formattedOrders
                    };
                })
                .filter(shop => shop.orders.length > 0);
        }
    },

    methods: {
        fetchData() {
            this.loading = true;
            this.$store
                .dispatch("driver/zone/fetch")
                .then(() => {
                    this.zones = this.resources || [];
                    this.$nextTick(() => {
                        const idFromUrl = this.$route.query.id;
                        if (idFromUrl) {
                            this.model.zone_id = Number(idFromUrl);
                        }
                    });
                })
                .finally(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                });
        },

        getDrivers() {
            this.$store
                .dispatch("driver/zone/getDrivers")
                .then(response => {
                    this.availableDrivers = response;
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                });
        },

        // Helper method: Determines if the shop button should show "Selected"
        isShopSelected(shop) {
            if (!shop.orders || shop.orders.length === 0) return false;
            // Reactive check: If every order of this shop is in the selection array
            return shop.orders.every(o => this.selectedOrders.includes(o.id));
        },

        toggleShopSelection(shopId) {
            const shop = this.displayShops.find(s => s.id === shopId);
            if (!shop) return;

            const shopOrderIds = shop.orders.map(o => o.id);
            const isAlreadyFullSelected = this.isShopSelected(shop);

            if (isAlreadyFullSelected) {
                // DESELECT: Remove all order IDs of this shop from the array
                this.selectedOrders = this.selectedOrders.filter(
                    id => !shopOrderIds.includes(id)
                );
            } else {
                // SELECT: Add order IDs (avoiding duplicates)
                shopOrderIds.forEach(id => {
                    if (!this.selectedOrders.includes(id)) {
                        this.selectedOrders.push(id);
                    }
                });
            }
        },

        removeShopOrders(shopId) {
            const shop = this.displayShops.find(s => s.id === shopId);
            if (shop) {
                const orderIdsToRemove = shop.orders.map(o => o.id);
                // Filtering the array automatically updates isShopSelected() and the button UI
                this.selectedOrders = this.selectedOrders.filter(
                    id => !orderIdsToRemove.includes(id)
                );
            }
        },

        assignDriver() {
            //Set driver ID from the selected dropdown
            this.model.driver_id = this.selectedDriver;

            // Set pickup request IDs (these are the selected order IDs)
            this.model.pickup_request_ids = this.selectedOrders;

            //Extract unique shop IDs from selected orders
            const selectedShopIds = new Set();
            this.selectedOrders.forEach(orderId => {
                const shop = this.displayShops.find(s =>
                    s.orders.some(o => o.id === orderId)
                );
                if (shop) {
                    selectedShopIds.add(shop.id);
                }
            });
            this.model.shop_ids = Array.from(selectedShopIds);

            //Set loading state to true
            this.fetch_data = true;

            //Send data to backend via Vuex action
            this.$store
                .dispatch("driver/zone/assignDriver", this.model)
                .then(resp => {
                    this.$notify({ type: "success", text: resp.message });

                    // Reset selections after successful assignment
                    this.selectedOrders = [];
                    this.selectedDriver = "";

                    this.fetchData();
                })
                .catch(error => {
                    // Error: Handle validation errors and show error notification
                    this.errors = new Errors(error.errors);
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.fetch_data = false;
                });
        },
        filterDriverOption(input, option) {
            // This looks at the text inside the <a-select-option>
            const driverName = option.componentOptions.children[0].text.toLowerCase();
            return driverName.indexOf(input.toLowerCase()) >= 0;
        },

        filterZoneOption(input, option) {
            // This compares the search text to the zone name text
            return (
                option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
            );
        },
        goBack() {
            if (this.$router) {
                this.$router.push("/admin/driver/zones");
            }
        }
    },
    watch: {
        // 1. When the dropdown changes (Number) -> Update URL (String)
        "model.zone_id"(newId) {
            // Use loose inequality (!=) to compare Number to String from URL
            if (newId != this.$route.query.id) {
                this.$router
                    .push({
                        query: { ...this.$route.query, id: newId }
                    })
                    .catch(err => {
                        if (err.name !== "NavigationDuplicated") throw err;
                    });
            }
        },

        // 2. When the URL changes (String) -> Update model (Number)
        "$route.query.id": {
            handler(newId) {
                if (newId) {
                    // Convert URL string back to number for the v-model.number
                    this.model.zone_id = Number(newId);
                }
            },
            immediate: true
        }
    }
};
</script>

<style scoped>
/* Makes the Ant Select height and border match your Tailwind design */
:deep(.ant-select-selection--single) {
    height: 42px !important;
    border-radius: 8px !important;
    border: 1px solid #d1d5db !important; /* gray-300 */
    display: flex;
    align-items: center;
}

/* Dark mode support */
.dark :deep(.ant-select-selection) {
    background-color: #374151 !important; /* gray-700 */
    border-color: #4b5563 !important; /* gray-600 */
    color: white !important;
}

:deep(.ant-select-selection__placeholder) {
    color: #9ca3af !important; /* gray-400 */
}
/* Custom scrollbar styling */
.tw-overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.tw-overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.5);
    border-radius: 3px;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.7);
}
</style>
